import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    IconButton,
    Chip,
    Snackbar,
    Alert,
    Collapse,
    Paper,
    Skeleton,
    CircularProgress // Added for loading state
} from '@mui/material';
import {
    ContentCopy as ContentCopyIcon,
    AccountCircle as AccountCircleIcon,
    Link as LinkIcon,
    ExpandLess,
    ExpandMore,
    ErrorOutline as ErrorOutlineIcon // For error state
} from '@mui/icons-material';
import { createTheme, ThemeProvider, styled, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import apiCall from '../../utils/apiCall';

// Function to fetch user connection data from the API
const fetchUserData = async (userId) => {
    try {
        console.log(`Fetching connection data for userId: ${ userId }`);
        const response = await apiCall(`/explorer/user/${ userId }/connection`, 'GET');
        return response;
    } catch (error) {
        console.error('Error fetching user connection data:', error);
        throw error;
    }
};


// A light theme for the component
const theme = createTheme({
    palette: {
        mode: 'light',
        primary: {
            main: '#1976d2',
        },
        secondary: {
            main: '#dc004e',
        },
        background: {
            default: '#f4f6f8',
            paper: '#ffffff',
        },
    },
    typography: {
        fontFamily: 'Roboto, Arial, sans-serif',
        h5: {
            fontWeight: 600,
        },
    },
    components: {
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                }
            }
        }
    }
});

const StyledCard = styled(Card)(({ theme }) => ({
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
}));

const ExpandableHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2),
    cursor: 'pointer',
    borderBottom: `1px solid ${ theme.palette.divider }`,
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
    },
}));

const UserConnections = ({ userId }) => {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [addressesOpen, setAddressesOpen] = useState(false);
    const [usersOpen, setUsersOpen] = useState(false);

    // Pagination state for addresses
    const [addressesPage, setAddressesPage] = useState(0);
    const [addressesRowsPerPage, setAddressesRowsPerPage] = useState(5);

    // Pagination state for users
    const [usersPage, setUsersPage] = useState(0);
    const [usersRowsPerPage, setUsersRowsPerPage] = useState(5);

    const currentTheme = useTheme();

    useEffect(() => {
        if (!userId) {
            setLoading(false);
            setError(t('explorer.noUserIdProvided'));
            return;
        }

        const loadData = async () => {
            try {
                setLoading(true);
                setError(null);
                const result = await fetchUserData(userId);
                setData(result);
                // Reset pagination when new data loads
                setAddressesPage(0);
                setUsersPage(0);
            } catch (err) {
                console.error('Failed to load user connection data:', err);
                setError(err.message || t('explorer.failedToLoadConnectionData'));
                setData(null);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [userId]); // Dependency array now includes userId


    const handleCopyToClipboard = (e, text) => {
        e.stopPropagation(); // Prevent the collapse from toggling
        const textArea = document.createElement('textarea');
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
            document.execCommand('copy');
            setSnackbarMessage(t('common.copied'));
            setSnackbarOpen(true);
        } catch (err) {
            setSnackbarMessage(t('explorer.failedToCopy'));
            setSnackbarOpen(true);
        }
        document.body.removeChild(textArea);
    };

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbarOpen(false);
    };

    // Pagination handlers for addresses
    const handleAddressesPageChange = (event, newPage) => {
        setAddressesPage(newPage);
    };

    const handleAddressesRowsPerPageChange = (event) => {
        setAddressesRowsPerPage(parseInt(event.target.value, 10));
        setAddressesPage(0);
    };

    // Pagination handlers for users
    const handleUsersPageChange = (event, newPage) => {
        setUsersPage(newPage);
    };

    const handleUsersRowsPerPageChange = (event) => {
        setUsersRowsPerPage(parseInt(event.target.value, 10));
        setUsersPage(0);
    };

    const getLabelColor = (label) => {
        if (!label) return 'default';
        if (label.toLowerCase().includes('deposit')) return 'success';
        if (label.toLowerCase().includes('withdraw')) return 'warning';
        return 'default';
    };

    const renderAddressesTableSkeleton = () => {
        return (
            <Box>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('explorer.address')}</TableCell>
                                <TableCell>{t('explorer.label')}</TableCell>
                                <TableCell align="right">{t('explorer.actions')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {Array.from({ length: 5 }).map((_, index) => (
                                <TableRow key={index}>
                                    <TableCell sx={{ maxWidth: '300px' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Skeleton variant="circular" width={16} height={16} />
                                            <Skeleton variant="text" width="80%" height={20} />
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Skeleton variant="rounded" width={60} height={24} />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Skeleton variant="circular" width={32} height={32} />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
                    <Skeleton variant="text" width={120} height={20} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Skeleton variant="text" width={80} height={20} />
                        <Skeleton variant="rectangular" width={60} height={32} />
                        <Skeleton variant="circular" width={32} height={32} />
                        <Skeleton variant="circular" width={32} height={32} />
                    </Box>
                </Box>
            </Box>
        );
    };

    const renderUsersTableSkeleton = () => {
        return (
            <Box>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('explorer.userId')}</TableCell>
                                <TableCell align="right">{t('explorer.actions')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {Array.from({ length: 5 }).map((_, index) => (
                                <TableRow key={index}>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Skeleton variant="circular" width={16} height={16} />
                                            <Skeleton variant="text" width="90%" height={20} />
                                        </Box>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Skeleton variant="circular" width={32} height={32} />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
                    <Skeleton variant="text" width={120} height={20} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Skeleton variant="text" width={80} height={20} />
                        <Skeleton variant="rectangular" width={60} height={32} />
                        <Skeleton variant="circular" width={32} height={32} />
                        <Skeleton variant="circular" width={32} height={32} />
                    </Box>
                </Box>
            </Box>
        );
    };

    const renderAddressesTable = () => {
        if (!data.addresses || data.addresses.length === 0) {
            return (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        {t('explorer.noAddressesFound')}
                    </Typography>
                </Box>
            );
        }

        // Calculate pagination
        const startIndex = addressesPage * addressesRowsPerPage;
        const endIndex = startIndex + addressesRowsPerPage;
        const paginatedAddresses = data.addresses.slice(startIndex, endIndex);

        return (
            <Box>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('explorer.address')}</TableCell>
                                <TableCell>{t('explorer.label')}</TableCell>
                                <TableCell align="right">{t('explorer.actions')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedAddresses.map((addr, index) => (
                                <TableRow key={startIndex + index} hover>
                                    <TableCell sx={{ wordBreak: 'break-all', maxWidth: '300px' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <LinkIcon color="action" fontSize="small" />
                                            <Typography variant="body2">
                                                {addr.address}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={addr.label}
                                            size="small"
                                            color={getLabelColor(addr.label)}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton
                                            size="small"
                                            onClick={(e) => handleCopyToClipboard(e, addr.address)}
                                            title={t('explorer.copyAddress')}
                                        >
                                            <ContentCopyIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={data.addresses.length}
                    rowsPerPage={addressesRowsPerPage}
                    page={addressesPage}
                    onPageChange={handleAddressesPageChange}
                    onRowsPerPageChange={handleAddressesRowsPerPageChange}
                    labelRowsPerPage={t('explorer.rowsPerPage')}
                    labelDisplayedRows={({ from, to, count }) =>
                        t('explorer.displayedRows', { from, to, count: count !== -1 ? count : `${ t('explorer.moreThan') } ${ to }` })
                    }
                />
            </Box>
        );
    };

    const renderUsersTable = () => {
        if (!data.users || data.users.length === 0) {
            return (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        {t('explorer.noUsersFound')}
                    </Typography>
                </Box>
            );
        }

        // Calculate pagination
        const startIndex = usersPage * usersRowsPerPage;
        const endIndex = startIndex + usersRowsPerPage;
        const paginatedUsers = data.users.slice(startIndex, endIndex);

        return (
            <Box>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('explorer.userId')}</TableCell>
                                <TableCell align="right">{t('explorer.actions')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedUsers.map((user, index) => (
                                <TableRow key={startIndex + index} hover>
                                    <TableCell sx={{ wordBreak: 'break-all' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <AccountCircleIcon color="action" fontSize="small" />
                                            <Typography variant="body2">
                                                {user}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton
                                            size="small"
                                            onClick={(e) => handleCopyToClipboard(e, user)}
                                            title={t('explorer.copyUserId')}
                                        >
                                            <ContentCopyIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={data.users.length}
                    rowsPerPage={usersRowsPerPage}
                    page={usersPage}
                    onPageChange={handleUsersPageChange}
                    onRowsPerPageChange={handleUsersRowsPerPageChange}
                    labelRowsPerPage={t('explorer.rowsPerPage')}
                    labelDisplayedRows={({ from, to, count }) =>
                        t('explorer.displayedRows', { from, to, count: count !== -1 ? count : `${ t('explorer.moreThan') } ${ to }` })
                    }
                />
            </Box>
        );
    };

    if (loading) {
        return (
            <Box sx={{ p: 3, backgroundColor: 'background.default' }}>
                <Grid container spacing={3}>
                    {/* Addresses Loading Skeleton */}
                    <Grid item xs={12} md={6}>
                        <StyledCard>
                            <ExpandableHeader>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Skeleton variant="circular" width={24} height={24} />
                                    <Skeleton variant="text" width={180} height={28} />
                                    <Skeleton variant="rounded" width={40} height={24} />
                                </Box>
                                <Skeleton variant="circular" width={24} height={24} />
                            </ExpandableHeader>
                            <CardContent sx={{ p: 0 }}>
                                {renderAddressesTableSkeleton()}
                            </CardContent>
                        </StyledCard>
                    </Grid>

                    {/* Users Loading Skeleton */}
                    <Grid item xs={12} md={6}>
                        <StyledCard>
                            <ExpandableHeader>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Skeleton variant="circular" width={24} height={24} />
                                    <Skeleton variant="text" width={160} height={28} />
                                    <Skeleton variant="rounded" width={40} height={24} />
                                </Box>
                                <Skeleton variant="circular" width={24} height={24} />
                            </ExpandableHeader>
                            <CardContent sx={{ p: 0 }}>
                                {renderUsersTableSkeleton()}
                            </CardContent>
                        </StyledCard>
                    </Grid>
                </Grid>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', p: 3, minHeight: '200px' }}>
                <ErrorOutlineIcon color="error" sx={{ fontSize: 48 }} />
                <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>
            </Box>
        );
    }

    if (!data) return null;

    return (
        <Box sx={{ p: 3, backgroundColor: 'background.default' }}>
            <Grid container spacing={3}>
                {/* Addresses List */}
                <Grid item xs={12} md={6}>
                    <StyledCard>
                        <ExpandableHeader onClick={() => setAddressesOpen(!addressesOpen)}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LinkIcon color="primary" />
                                <Typography variant="h6">{t('explorer.associatedAddresses')}</Typography>
                                <Chip
                                    label={loading ? "..." : (data.associatedAddressCount || data.addresses?.length || 0)}
                                    color="primary"
                                    size="small"
                                />
                            </Box>
                            {addressesOpen ? <ExpandLess /> : <ExpandMore />}
                        </ExpandableHeader>
                        <Collapse in={addressesOpen} timeout="auto" unmountOnExit>
                            <CardContent sx={{ p: 0 }}>
                                {loading ? renderAddressesTableSkeleton() : renderAddressesTable()}
                            </CardContent>
                        </Collapse>
                        {!addressesOpen && (
                            <Box sx={{ p: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    {loading ? (
                                        <Skeleton variant="text" width="60%" height={20} />
                                    ) : (
                                        t('explorer.clickToExpandAddresses', { count: data.addresses?.length || 0 })
                                    )}
                                </Typography>
                            </Box>
                        )}
                    </StyledCard>
                </Grid>

                {/* Users List */}
                <Grid item xs={12} md={6}>
                    <StyledCard>
                        <ExpandableHeader onClick={() => setUsersOpen(!usersOpen)}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <AccountCircleIcon color="primary" />
                                <Typography variant="h6">{t('explorer.associatedUsers')}</Typography>
                                <Chip
                                    label={loading ? "..." : (data.associatedUserCount || data.users?.length || 0)}
                                    color="primary"
                                    size="small"
                                />
                            </Box>
                            {usersOpen ? <ExpandLess /> : <ExpandMore />}
                        </ExpandableHeader>
                        <Collapse in={usersOpen} timeout="auto" unmountOnExit>
                            <CardContent sx={{ p: 0 }}>
                                {loading ? renderUsersTableSkeleton() : renderUsersTable()}
                            </CardContent>
                        </Collapse>
                        {!usersOpen && (
                            <Box sx={{ p: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    {loading ? (
                                        <Skeleton variant="text" width="60%" height={20} />
                                    ) : (
                                        t('explorer.clickToExpandUsers', { count: data.users?.length || 0 })
                                    )}
                                </Typography>
                            </Box>
                        )}
                    </StyledCard>
                </Grid>
            </Grid>

            <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={handleSnackbarClose}>
                <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default UserConnections;